///
/// EarthBrowser - KML/KMZ Handler
///
/// Handles KML and KMZ drag & drop by mapping onto existing import
/// pipelines: GroundOverlay elements become image ground overlays at
/// their LatLonBox, placemark geometry becomes a GeoJSON dataset (data
/// browser window, cloud save, story capture — all for free). KMZ
/// archives resolve overlay images from the archive itself.
/// Created by Matt Giger on 9/24/2026
/// Copyright (c) 2026 Geodata Labs
///

import { registerDatasetSourceFile } from '@/components/features/user-datasets/datasetSourceFiles'
import { toast } from '@/components/ui/use-toast'
import { cMaxGroundOverlays } from '@/earthbrowser/dataset-distribution/ground-overlay/GroundOverlays'
import { ZipArchive } from '@/earthbrowser/utils/ZipArchive'
import { parseKml, type KmlGroundOverlay } from '@/utils/kmlImport'
import { mountImageOverlay } from '@/utils/imageOverlayMount'
import { BaseFileHandler } from './BaseFileHandler'
import { GeoJSONFileHandler } from './GeoJSONFileHandler'

export class KMLFileHandler extends BaseFileHandler {
	private geojsonHandler: GeoJSONFileHandler | null = null

	canHandle(file: File): boolean {
		return /\.km[lz]$/i.test(file.name)
	}

	initialize(): void {}

	cleanup(): void {
		this.geojsonHandler?.cleanup()
		this.geojsonHandler = null
	}

	async handleFile(file: File): Promise<string | null> {
		try {
			const isKmz = /\.kmz$/i.test(file.name)
			let kmlText: string
			let archive: ZipArchive | null = null
			let archiveBuffer: ArrayBuffer | null = null

			if (isKmz) {
				archiveBuffer = await file.arrayBuffer()
				archive = new ZipArchive(archiveBuffer)
				const kmlEntry = archive.files.find(f => f.name.toLowerCase().endsWith('.kml'))
				if (!kmlEntry) throw new Error('KMZ archive contains no KML document')
				kmlText = new TextDecoder('utf8').decode(await kmlEntry.extract())
			} else {
				kmlText = await file.text()
			}

			const parsed = parseKml(kmlText)
			for (const warning of parsed.warnings) {
				console.warn(`[kml] ${file.name}: ${warning}`)
			}
			if (parsed.groundOverlays.length === 0 && !parsed.geojson) {
				toast({
					title: 'Nothing to import',
					description: `${file.name} has no placemark geometry or ground overlays.`,
					duration: 6000,
				})
				return null
			}

			let firstWindowId: string | null = null
			const baseName = file.name.replace(/\.km[lz]$/i, '')

			// Placemark geometry rides the standard GeoJSON pipeline
			if (parsed.geojson) {
				if (!this.geojsonHandler) {
					this.geojsonHandler = new GeoJSONFileHandler(this.context)
					this.geojsonHandler.initialize()
				}
				const geojsonFile = new File([parsed.geojson], `${baseName}.geojson`, { type: 'application/geo+json' })
				firstWindowId = await this.geojsonHandler.handleFile(geojsonFile)
			}

			// Ground overlays mount at their LatLonBox, capped at the engine slot count
			const overlays = parsed.groundOverlays.slice(0, cMaxGroundOverlays)
			if (parsed.groundOverlays.length > overlays.length) {
				toast({
					title: 'Too many overlays',
					description: `${file.name} has ${parsed.groundOverlays.length} ground overlays; only the first ${cMaxGroundOverlays} were imported.`,
					duration: 6000,
				})
			}
			for (const overlay of overlays) {
				const imageFile = await this.resolveOverlayImage(overlay, archive)
				if (!imageFile) {
					console.warn(`[kml] ${file.name}: could not load overlay image "${overlay.href}"`)
					continue
				}
				const windowId = await mountImageOverlay(this.context.dispatch, this.context.getState, imageFile, {
					bounds: overlay.bounds,
					opacity: overlay.opacity,
				})
				if (windowId) {
					registerDatasetSourceFile(windowId, { file: imageFile, format: 'image' })
					firstWindowId = firstWindowId ?? windowId
				}
			}

			if (!firstWindowId) {
				toast({
					title: 'KML import failed',
					description: `Nothing in ${file.name} could be imported.`,
					duration: 6000,
				})
			}
			return firstWindowId
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error)
			console.error(`Failed to import KML ${file.name}:`, error)
			toast({
				title: 'KML import failed',
				description: message,
				duration: 8000,
			})
			return null
		}
	}

	/** Resolve a GroundOverlay Icon href: KMZ archive entry or remote URL */
	private async resolveOverlayImage(overlay: KmlGroundOverlay, archive: ZipArchive | null): Promise<File | null> {
		const href = overlay.href.split(/[?#]/)[0]
		const filename = href.split('/').pop() || 'overlay.png'
		try {
			if (/^https?:\/\//i.test(href)) {
				const response = await fetch(href)
				if (!response.ok) return null
				const blob = await response.blob()
				return new File([blob], filename, { type: blob.type || 'image/png' })
			}
			if (archive) {
				const normalized = href.replace(/^\.?\//, '').toLowerCase()
				const entry = archive.files.find(f => f.name.toLowerCase() === normalized)
					?? archive.files.find(f => f.name.toLowerCase().endsWith('/' + filename.toLowerCase()))
					?? archive.files.find(f => f.name.toLowerCase() === filename.toLowerCase())
				if (!entry) return null
				const data = await entry.extract() as ArrayBuffer
				return new File([data], filename, { type: mimeForName(filename) })
			}
			return null
		} catch (error) {
			console.warn(`[kml] Failed to load overlay image ${href}:`, error)
			return null
		}
	}
}

function mimeForName(name: string): string {
	if (/\.jpe?g$/i.test(name)) return 'image/jpeg'
	if (/\.webp$/i.test(name)) return 'image/webp'
	if (/\.gif$/i.test(name)) return 'image/gif'
	return 'image/png'
}
